/**
 * Generates a photo template for a given vehicle type
 * @param {string} vehicleType - The type of vehicle (sedan, suv, truck, van, coupe, hatchback, wagon, convertible)
 * @returns {Array} Array of photo objects with name, description, category, and tags
 */
import { FiArrowUpLeft, FiArrowUpRight, FiArrowDownLeft, FiArrowDownRight } from "react-icons/fi";
import { IoMdArrowDown, IoMdArrowUp, IoMdArrowForward, IoMdArrowBack } from "react-icons/io";

export const getPhotoTemplate = (vehicleType) => {
    // Base photos common to all vehicle types
    const basePhotos = [
        { id: 1, name: 'Front View', description: 'Center front of vehicle', category: 'exterior', tags: ['front', 'grille', 'headlights', 'bumper', 'license-plate'], icon: IoMdArrowDown },
        { id: 2, name: 'Rear View', description: 'Center rear of vehicle', category: 'exterior', tags: ['rear', 'taillights', 'bumper', 'license-plate', 'exhaust'], icon: IoMdArrowUp },
        { id: 3, name: 'Left Side (Driver Door in View)', description: 'Full side profile', category: 'exterior', tags: ['side', 'profile', 'doors', 'windows', 'body-lines', 'driver-side'], icon: IoMdArrowBack },
        { id: 4, name: 'Right Side (Passenger Door in View)', description: 'Full side profile', category: 'exterior', tags: ['side', 'profile', 'doors', 'windows', 'body-lines', 'passenger-side'], icon: IoMdArrowForward },
        { id: 5, name: 'Down Left Angle (Driver Door in View)', description: 'Vehicle facing forward, angled down left', category: 'exterior', tags: ['angle', 'front-left', '3-4-view', 'driver-side', 'front'], icon: FiArrowDownLeft },
        { id: 6, name: 'Down Right Angle (Passenger Door in View)', description: 'Vehicle facing forward, angled down right', category: 'exterior', tags: ['angle', 'front-right', '3-4-view', 'passenger-side', 'front'], icon: FiArrowDownRight },
        { id: 7, name: 'Up Right Angle (Passenger Door in View)', description: 'Vehicle facing backward, angled up right', category: 'exterior', tags: ['angle', 'rear-right', '3-4-view', 'passenger-side', 'rear'], icon: FiArrowUpRight },
        { id: 8, name: 'Up Left Angle (Driver Door in View)', description: 'Vehicle facing backward, angled up left', category: 'exterior', tags: ['angle', 'rear-left', '3-4-view', 'driver-side', 'rear'], icon: FiArrowUpLeft },
        { id: 9, name: 'Dashboard', description: 'Full dashboard and steering wheel', category: 'interior', tags: ['interior', 'dashboard', 'steering-wheel', 'gauges', 'controls', 'infotainment'] },
        { id: 10, name: 'Front Seats', description: 'Driver and passenger seats', category: 'interior', tags: ['interior', 'seats', 'front-seats', 'upholstery', 'comfort'] },
        { id: 11, name: 'Back Seats', description: 'Rear seats', category: 'interior', tags: ['interior', 'seats', 'back-seats', 'upholstery', 'comfort'] },
        { id: 12, name: 'Center Console', description: 'Gear shifter and controls', category: 'interior', tags: ['interior', 'console', 'shifter', 'controls', 'cupholders', 'storage'] },
        { id: 13, name: 'Odometer', description: 'Close-up of mileage display', category: 'detail', tags: ['interior', 'odometer', 'mileage', 'gauges', 'instrument-cluster'] },
        { id: 14, name: 'Infotainment System', description: 'Touchscreen display and controls', category: 'detail', tags: ['interior', 'infotainment', 'touchscreen', 'display', 'navigation', 'audio', 'technology', 'entertainment'] },
        { id: 15, name: 'Climate Controls', description: 'HVAC and climate control panel', category: 'detail', tags: ['interior', 'climate-control', 'hvac', 'ac', 'heating', 'controls', 'dual-zone'] },
        { id: 16, name: 'Power Seats', description: 'Power seat controls and adjustments', category: 'detail', tags: ['interior', 'power-seats', 'seat-controls', 'adjustable', 'memory-seats', 'comfort', 'lumbar'] },
        { id: 17, name: 'Sunroof/Moonroof', description: 'Sunroof or panoramic roof view', category: 'detail', tags: ['interior', 'sunroof', 'moonroof', 'panoramic-roof', 'skylight', 'glass-roof', 'feature'] },
        { id: 18, name: 'Engine Bay', description: 'Under the hood', category: 'detail', tags: ['exterior', 'engine', 'motor', 'under-hood', 'mechanical'] },
        { id: 19, name: 'Wheels/Tires', description: 'Close-up of wheel and tire', category: 'detail', tags: ['exterior', 'wheels', 'tires', 'rims', 'brakes'] },
        { id: 20, name: 'Backup Camera (Interior)', description: 'Dashboard screen showing backup camera view', category: 'detail', tags: ['interior', 'backup-camera', 'camera', 'screen', 'infotainment', 'safety', 'display'] },
        { id: 21, name: 'Backup Camera (Exterior)', description: 'Rear-mounted backup camera unit', category: 'detail', tags: ['exterior', 'backup-camera', 'camera', 'rear', 'safety', 'technology'] },
        { id: 22, name: 'Keyless Entry/Start', description: 'Key fob and push-button start', category: 'detail', tags: ['interior', 'keyless-entry', 'push-button-start', 'key-fob', 'remote', 'convenience', 'ignition'] },
        { id: 23, name: 'USB/Charging Ports', description: 'USB ports and charging options', category: 'detail', tags: ['interior', 'usb', 'charging', 'ports', 'connectivity', 'wireless-charging', 'technology'] },
        { id: 24, name: 'Storage Compartments', description: 'Glovebox, door pockets, and storage', category: 'detail', tags: ['interior', 'storage', 'glovebox', 'compartments', 'door-pockets', 'organization', 'utility'] },
        { id: 51, name: 'Trunk Storage', description: 'Organized trunk/cargo storage, underfloor compartment, or spare tire area', category: 'detail', tags: ['interior', 'trunk', 'cargo', 'storage', 'organization', 'underfloor', 'spare-tire'] }
    ];

    // Vehicle-specific additional photos
    const vehicleSpecificPhotos = {
        sedan: [
            { id: 25, name: 'Rear Seats', description: 'Back seat area', category: 'interior', tags: ['interior', 'seats', 'rear-seats', 'back-seat', 'upholstery'] },
            { id: 26, name: 'Trunk', description: 'Trunk/cargo area', category: 'interior', tags: ['interior', 'trunk', 'cargo', 'storage', 'cargo-space'] }
        ],
        suv: [
            { id: 27, name: 'Second Row Seats', description: 'Second row seating', category: 'interior', tags: ['interior', 'seats', 'second-row', 'middle-seats', 'upholstery', 'passenger-space'] },
            { id: 28, name: 'Third Row Seats', description: 'Third row seating (if applicable)', category: 'interior', tags: ['interior', 'seats', 'third-row', 'back-seats', 'upholstery', '7-seater', '8-seater'] },
            { id: 29, name: 'Cargo Area', description: 'Rear cargo space', category: 'interior', tags: ['interior', 'cargo', 'storage', 'trunk', 'cargo-space', 'capacity'] },
            { id: 30, name: 'Running Boards', description: 'Side steps/running boards', category: 'detail', tags: ['exterior', 'running-boards', 'side-steps', 'entry', 'accessories'] }
        ],
        truck: [
            { id: 31, name: 'Rear Seats', description: 'Back seat area (crew cab)', category: 'interior', tags: ['interior', 'seats', 'rear-seats', 'back-seat', 'upholstery', 'crew-cab'] },
            { id: 32, name: 'Truck Bed', description: 'Pickup bed/cargo area', category: 'exterior', tags: ['exterior', 'bed', 'cargo', 'hauling', 'payload'] },
            { id: 33, name: 'Tailgate Open', description: 'Tailgate in open position', category: 'detail', tags: ['exterior', 'tailgate', 'open', 'access', 'bed'] },
            { id: 34, name: 'Tailgate Closed', description: 'Tailgate in closed position', category: 'detail', tags: ['exterior', 'tailgate', 'closed', 'bed'] },
            { id: 35, name: 'Bed Liner', description: 'Interior of truck bed', category: 'detail', tags: ['exterior', 'bed-liner', 'bed', 'protection', 'cargo'] }
        ],
        van: [
            { id: 36, name: 'Second Row Seats', description: 'Second row seating', category: 'interior', tags: ['interior', 'seats', 'second-row', 'middle-seats', 'upholstery', 'passenger-space', 'captain-chairs'] },
            { id: 37, name: 'Third Row Seats', description: 'Third row seating', category: 'interior', tags: ['interior', 'seats', 'third-row', 'back-seats', 'upholstery', '7-seater', '8-seater'] },
            { id: 38, name: 'Cargo Area', description: 'Rear cargo space', category: 'interior', tags: ['interior', 'cargo', 'storage', 'cargo-space', 'capacity', 'stow-n-go'] },
            { id: 39, name: 'Sliding Doors', description: 'Side sliding door mechanism', category: 'detail', tags: ['exterior', 'sliding-door', 'side-door', 'entry', 'access', 'power-doors'] }
        ],
        coupe: [
            { id: 40, name: 'Rear Seats', description: 'Back seat area', category: 'interior', tags: ['interior', 'seats', 'rear-seats', 'back-seat', 'upholstery'] },
            { id: 41, name: 'Trunk', description: 'Trunk/cargo area', category: 'interior', tags: ['interior', 'trunk', 'cargo', 'storage', 'cargo-space'] }
        ],
        hatchback: [
            { id: 42, name: 'Rear Seats', description: 'Back seat area', category: 'interior', tags: ['interior', 'seats', 'rear-seats', 'back-seat', 'upholstery', 'fold-down'] },
            { id: 43, name: 'Hatchback/Cargo', description: 'Rear hatch and cargo area', category: 'interior', tags: ['interior', 'hatch', 'cargo', 'storage', 'cargo-space', 'versatility'] }
        ],
        wagon: [
            { id: 44, name: 'Rear Seats', description: 'Back seat area', category: 'interior', tags: ['interior', 'seats', 'rear-seats', 'back-seat', 'upholstery', 'fold-down'] },
            { id: 45, name: 'Cargo Area', description: 'Rear cargo space', category: 'interior', tags: ['interior', 'cargo', 'storage', 'cargo-space', 'capacity', 'versatility'] },
            { id: 46, name: 'Roof Rails', description: 'Roof rack system', category: 'detail', tags: ['exterior', 'roof-rails', 'roof-rack', 'cargo', 'utility', 'accessories'] }
        ],
        convertible: [
            { id: 47, name: 'Top Down', description: 'Overhead view with top down', category: 'exterior', tags: ['exterior', 'top-down', 'open', 'roadster', 'cabriolet'] },
            { id: 48, name: 'Top Up', description: 'Side view with top up', category: 'exterior', tags: ['exterior', 'top-up', 'closed', 'hardtop', 'softtop'] },
            { id: 49, name: 'Rear Seats', description: 'Back seat area', category: 'interior', tags: ['interior', 'seats', 'rear-seats', 'back-seat', 'upholstery'] },
            { id: 50, name: 'Convertible Top Mechanism', description: 'Top mechanism and storage', category: 'detail', tags: ['interior', 'convertible-top', 'mechanism', 'folding', 'storage'] }
        ]
    };

    // Vehicle type-specific tag additions
    const typeSpecificTags = {
        suv: ['suv'],
        truck: ['truck', 'pickup'],
        van: ['van', 'minivan'],
        coupe: ['coupe', 'sport'],
        hatchback: ['hatchback', 'compact'],
        wagon: ['wagon', 'estate'],
        convertible: ['convertible', 'roadster']
    };

    // Special tag additions for specific photos by vehicle type
    const specialPhotoTags = {
        suv: {
            'Driver Side': ['ground-clearance'],
            'Passenger Side': ['ground-clearance'],
            'Wheels/Tires': ['all-terrain'],
            'Rear View': ['suv'],
            'Front View': ['suv']
        },
        truck: {
            'Driver Side': ['cab', 'bed'],
            'Passenger Side': ['cab', 'bed'],
            'Rear View': ['bed'],
            'Up Right Angle (Passenger Side in View)': ['bed'],
            'Up Left Angle (Driver Side in View)': ['bed'],
            'Rear Seats': ['truck'],
            'Tailgate Open': ['truck', 'pickup'],
            'Tailgate Closed': ['truck', 'pickup'],
            'Truck Bed': ['truck', 'pickup'],
            'Engine Bay': ['power'],
            'Wheels/Tires': ['off-road'],
            'Bed Liner': ['truck', 'pickup']
        },
        van: {
            'Driver Side': ['sliding-door'],
            'Passenger Side': ['sliding-door'],
            'Rear View': ['rear-doors'],
            'Sliding Doors': ['van', 'minivan']
        },
        coupe: {
            'Driver Side': ['2-door'],
            'Passenger Side': ['2-door'],
            'Front Seats': ['bucket-seats'],
            'Engine Bay': ['performance'],
            'Wheels/Tires': ['performance']
        },
        hatchback: {
            'Rear View': ['hatch'],
            'Hatchback/Cargo': ['hatchback']
        },
        wagon: {
            'Driver Side': ['long-roof'],
            'Passenger Side': ['long-roof'],
            'Rear View': ['tailgate'],
            'Cargo Area': ['wagon'],
            'Roof Rails': ['wagon']
        },
        convertible: {
            'Top Down': ['convertible'],
            'Top Up': ['convertible'],
            'Front Seats': ['sport'],
            'Convertible Top Mechanism': ['convertible'],
            'Engine Bay': ['performance'],
            'Wheels/Tires': ['performance']
        }
    };

    // Combine base photos with vehicle-specific photos
    const template = [
        ...basePhotos,
        ...(vehicleSpecificPhotos[vehicleType] || [])
    ];

    // Apply vehicle type tags to all photos
    const typeTags = typeSpecificTags[vehicleType] || [];
    const specialTags = specialPhotoTags[vehicleType] || {};

    return template.map(photo => ({
        ...photo,
        tags: [
            ...photo.tags,
            ...typeTags,
            ...(specialTags[photo.name] || [])
        ]
    }));
};
